import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { PublicLayout } from '../components/layout/PublicLayout'
import { Icons } from '../components/ui/Icons'
import { useMyChurches } from '../hooks/useChurches'
import { useAuthStore } from '../store/auth'

export default function HostEntryPage() {
  const { accessToken } = useAuthStore()
  const { data: myChurchesData } = useMyChurches()
  const myChurches = myChurchesData?.data ?? []
  const [churchPickerOpen, setChurchPickerOpen] = useState(false)
  const navigate = useNavigate()

  function handleChurchCardClick() {
    if (accessToken && myChurches.length > 0) {
      setChurchPickerOpen((v) => !v)
    } else {
      navigate('/churches/new')
    }
  }

  return (
    <PublicLayout activeNav="Host an event">
      <div className="max-w-[600px] mx-auto px-6 pt-10 pb-20">
        <div className="eyebrow mb-2">Host</div>
        <h1 className="serif m-0 mb-2 text-[40px] tracking-tight">
          Host an{' '}
          <span className="serif-i text-primary">event</span>
        </h1>
        <p className="text-sm text-ink-2 m-0 mb-9">
          Anyone can host a fellowship gathering — choose how you'd like to list it.
        </p>

        <div className="flex flex-col gap-3.5">
          <Link to="/events/new" className="no-underline">
            <div className="card px-7 py-6 flex items-center gap-5">
              <div className="w-[52px] h-[52px] rounded-[14px] shrink-0 bg-primary-soft flex items-center justify-center text-[26px]">
                🏠
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold text-ink mb-1">
                  Host as an individual
                </div>
                <div className="text-[13px] text-ink-2 leading-[1.5]">
                  Free forever · approve each guest · exact location shared only after you approve
                </div>
              </div>
              <Icons.arrow size={16} className="text-primary shrink-0" />
            </div>
          </Link>

          <div className="card overflow-hidden">
            <button
              type="button"
              onClick={handleChurchCardClick}
              className="w-full px-7 py-6 flex items-center gap-5 bg-transparent border-0 cursor-pointer text-left"
            >
              <div className="w-[52px] h-[52px] rounded-[14px] shrink-0 bg-primary-soft flex items-center justify-center text-[26px]">
                ⛪
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold text-ink mb-1">
                  Host as a church
                </div>
                <div className="text-[13px] text-ink-2 leading-[1.5]">
                  Subscription required · unlimited recurring events · coordinator team access
                </div>
              </div>
              <Icons.arrow
                size={16}
                className="text-primary shrink-0 transition-transform duration-150"
                style={{ transform: churchPickerOpen ? 'rotate(90deg)' : 'none' }}
              />
            </button>

            {churchPickerOpen && (
              <div className="border-t border-line-soft">
                {myChurches.map((church) => (
                  <Link
                    key={church.id}
                    to={`/churches/${church.slug}/events/new`}
                    className="no-underline flex items-center gap-3.5 px-7 py-4 hover:bg-surface-2 transition-colors border-b border-line-soft last:border-b-0"
                  >
                    <div className="w-9 h-9 rounded-[10px] bg-primary-soft text-primary-deep flex items-center justify-center font-semibold text-[15px] shrink-0 overflow-hidden">
                      {church.logoUrl
                        ? <img src={church.logoUrl} alt="" className="w-full h-full object-cover" />
                        : church.name.slice(0, 1).toUpperCase()
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13.5px] font-semibold text-ink truncate">{church.name}</div>
                      <div className="text-[11.5px] text-muted capitalize">{church.role}</div>
                    </div>
                    <Icons.arrow size={14} className="text-muted shrink-0" />
                  </Link>
                ))}
                <Link
                  to="/churches/new"
                  className="no-underline flex items-center gap-3.5 px-7 py-4 hover:bg-surface-2 transition-colors"
                >
                  <div className="w-9 h-9 rounded-[10px] border-2 border-dashed border-line flex items-center justify-center text-muted shrink-0">
                    <span className="text-[18px] leading-none">+</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-semibold text-ink-2">Register a new church</div>
                  </div>
                  <Icons.arrow size={14} className="text-muted shrink-0" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  )
}
